import React, { useState } from 'react';
import { string, object } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './DownloadButton.module.css';

/**
 * DownloadButton — allows transaction customers to download listing files.
 *
 * Calls POST /api/downloads/request to get a signed, time-limited S3 URL.
 * The file downloads automatically in the browser.
 *
 * Usage (in TransactionPanel or transaction detail page):
 *   {listing.attributes.publicData.files?.map(file => (
 *     <DownloadButton
 *       key={file.key}
 *       transactionId={transaction.id.uuid}
 *       file={file}
 *     />
 *   ))}
 *
 * @param {string} props.transactionId
 * @param {Object} props.file — { key, name, size, mimetype }
 * @param {string} [props.className]
 */
const DownloadButton = ({ transactionId, file, className }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    if (isLoading || !transactionId || !file?.key) return;

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/downloads/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, fileKey: file.key }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Download failed');
      }

      const { url, filename } = await res.json();

      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || file.name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBytes = bytes => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isLoading}
        className={css.button}
      >
        <svg className={css.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span className={css.filename}>{file.name || 'Download'}</span>
        {file.size && <span className={css.size}>{formatBytes(file.size)}</span>}
        {isLoading && <span className={css.loading}><FormattedMessage id="DownloadButton.loading" /></span>}
      </button>
      {error && <p className={css.error}>{error}</p>}
    </div>
  );
};

DownloadButton.defaultProps = { className: null };
DownloadButton.propTypes = {
  transactionId: string.isRequired,
  file: object.isRequired,
  className: string,
};

export default DownloadButton;
